"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { InternetPlan } from "@/lib/fetch/internet";

/**
 * The shared plan catalogue, fetched once per session from the cached public
 * endpoint and reused by search, compare, the finder and the compare tray.
 * Pages that already render a subset can pass it as `fallback` and only
 * enable the fetch when a plan outside that subset is needed.
 */
export const useInternetPlans = (
  options: { enabled?: boolean; fallback?: InternetPlan[] } = {},
) => {
  const query = useQuery({
    queryKey: ["resource", "internet", "plans"],
    enabled: options.enabled ?? true,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const res = await fetch("/api/site/public/resource/internet/plans", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to load internet plans");
      const result = (await res.json()) as {
        success: boolean;
        data: InternetPlan[];
      };
      return result.data;
    },
  });

  const plans = query.data ?? options.fallback ?? [];
  const byId = useMemo(
    () =>
      new Map(
        [...(options.fallback ?? []), ...(query.data ?? [])].map((plan) => [
          plan.id,
          plan,
        ]),
      ),
    [options.fallback, query.data],
  );

  return {
    plans,
    byId,
    isLoading: query.isLoading && !options.fallback,
    isError: query.isError,
    refetch: query.refetch,
  };
};
